import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { BankStageBadge, FormErrorSummary, SelectField, TextareaField, applyServerErrors, useConfigLabel, zRequiredString } from '@/business';
import { Alert, Button, Dialog, DialogContent, Skeleton, toast } from '@/design-system';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';

import { useRecordBankStage } from '../../api/mutations';
import { bankStagesQuery } from '../../api/queries';
import type { DealAction } from '../../model/stages';

const schema = z.object({ code: zRequiredString(), note: z.string().optional() });
type Values = z.infer<typeof schema>;

/**
 * Records the bank's operational stage from its catalogue. Any direction is allowed. When the stage maps to another
 * DealDesk stage the dialog offers the matching action; it never changes the macro stage by itself.
 */
export function BankStageDialog({ deal, onClose, onAction }: { deal: Schema<'Deal'>; onClose: () => void; onAction: (a: DealAction) => void }) {
  const { t } = useTranslation('deals');
  const label = useConfigLabel();
  const stages = useQuery(bankStagesQuery(deal.bank.id));
  const record = useRecordBankStage(deal.id);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [suggested, setSuggested] = useState<{ action: DealAction; stage: string } | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { code: '', note: '' } });
  const options = (stages.data ?? []).filter((s) => !s.deprecated && (!s.products.length || s.products.includes(deal.product))).map((s) => ({ value: s.code, label: `${s.code} · ${label(s.label, s.labelAr)}${s.reportingBucket === null ? ` (${t('bankStage.unmapped')})` : ''}` }));

  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      const res = await record.mutateAsync({ version: deal.version, body: { code: v.code, ...(v.note?.trim() ? { note: v.note.trim() } : {}) } });
      toast.done(t('bankStage.recorded', { code: res.event.code }));
      if (res.suggestedAction) setSuggested({ action: res.suggestedAction, stage: `${res.event.code} · ${res.event.label}` });
      else onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, ['code', 'note']);
        setProblem(e);
      }
    }
  });

  if (suggested) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          title={t('bankStage.suggestTitle')}
          footer={
            <>
              <Button onClick={onClose}>{t('bankStage.notNow')}</Button>
              <Button
                variant="primary"
                onClick={() => {
                  onClose();
                  onAction(suggested.action);
                }}
              >
                {t(`actions.${suggested.action}.label`)}
              </Button>
            </>
          }
        >
          <p className="text-body text-ink-secondary">{t('bankStage.suggestBody', { stage: suggested.stage, action: t(`actions.${suggested.action}.label`) })}</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        size="form"
        title={t('bankStage.title', { bank: deal.bank.code })}
        description={t('bankStage.body')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={record.isPending} disabled={!options.length} onClick={() => void submit()}>
              {t('bankStage.record')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
          {deal.bankStage ? (
            <p className="text-body text-ink-secondary">
              {t('bankStage.current')} <BankStageBadge stage={deal.bankStage} withSince />
            </p>
          ) : null}
          {stages.isPending ? (
            <Skeleton className="h-8 w-full" />
          ) : options.length ? (
            <SelectField control={form.control} name="code" label={t('bankStage.stage')} options={options} placeholder={t('form.selectPlaceholder', { ns: 'common' })} />
          ) : (
            <Alert tone="neutral" title={t('bankStage.noCatalogue', { bank: deal.bank.code })}>
              {t('bankStage.noCatalogueBody')}
            </Alert>
          )}
          <TextareaField control={form.control} name="note" label={t('bankStage.note')} optional rows={2} />
          <button type="submit" hidden />
        </form>
      </DialogContent>
    </Dialog>
  );
}
